import { MLRecommendationsConst, TaxonomyConst } from '@axiom/const';
import type { Opportunity, Position } from '@axiom/validation';

const { BlockingConditionsMap } = MLRecommendationsConst;

const getRequiredBillingHoursPerWeek = (positions?: Position[] | null) => {
  const positionsHours = (positions ?? []).reduce<number[]>(
    (acc, { billingHoursPerWeek = null }) =>
      billingHoursPerWeek !== null ? [...acc, billingHoursPerWeek] : acc,
    []
  );
  return positionsHours.length ? Math.ceil(Math.min(...positionsHours)) : null;
};

const getDesiredBusinessTitles = (positions: Position[] = []) => {
  const emptyTitles = positions
    .map(position => position.businessTitle)
    .filter(title => (title ?? '').trim() === '');

  if (emptyTitles.length > 0) {
    return null;
  }

  const uniqueTitles = [
    ...new Set(positions.map(position => position.businessTitle)),
  ];

  const wantsLawyer =
    uniqueTitles.length > 0 &&
    uniqueTitles.every(businessTitle =>
      [TaxonomyConst.LawyerBusinessTitle].includes(businessTitle)
    );
  const wantsLegalSupport =
    !wantsLawyer &&
    uniqueTitles.length > 0 &&
    uniqueTitles.every(
      businessTitle =>
        ![TaxonomyConst.LawyerBusinessTitle].includes(businessTitle)
    );

  return wantsLawyer || wantsLegalSupport
    ? uniqueTitles.filter(pos => pos !== null)
    : null;
};

const atLeastOnePositionHasDescription = (positions: Position[]) =>
  positions.length &&
  positions.some(({ description }) => !!description?.trim());

const determineBlockingCondition = (
  opportunity: Opportunity,
  positions: Position[] | undefined
) => {
  const { id, isClosed } = opportunity;
  if (!id) {
    return BlockingConditionsMap.oppDoesNotExist;
  }
  if (isClosed) {
    return BlockingConditionsMap.oppClosed;
  }
  if (!positions?.length) {
    return BlockingConditionsMap.noPositions;
  }
  if (!getDesiredBusinessTitles(positions)) {
    return BlockingConditionsMap.notAllPositionsAreAcceptablyTyped;
  }
  if (!atLeastOnePositionHasDescription(positions)) {
    return BlockingConditionsMap.positionDescriptionsAllEmpty;
  }
  return undefined;
};

// returns one of:
//  an override message - to show instead of the button
//  null - to indicate neither the button nor an override message should be shown
//  undefined - to indicate the button should be shown
const getOverrideMessage = (
  opportunity: Opportunity,
  positions: Position[] | undefined
) => {
  const blockingCondition = determineBlockingCondition(opportunity, positions);

  switch (blockingCondition) {
    case 'noPositions':
      return 'Add a position to get AI matches.';
    case 'notAllPositionsAreAcceptablyTyped':
      return 'AI matching requires same business title on all positions.';
    case 'positionDescriptionsAllEmpty':
      return 'Provide a position description to receive AI matches.';
    case 'oppClosed':
    default:
      return undefined;
  }
};

const getAiUnmatchableMessage = (opportunity: Opportunity) => {
  return getOverrideMessage(opportunity, opportunity.positions);
};

export const MlRecommendedUtil = {
  getRequiredBillingHoursPerWeek,
  getDesiredBusinessTitles,
  getAiUnmatchableMessage,
};
